import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { IDate } from "../../components/Table/hooks/useDateFilter";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  IGroupedInsuranceReports,
  IInsuranceReports,
} from "./insuranceReports.types";
import { INoPolicyInsuranceReports } from "../../views/tasks/InsuranceReports/insuranceReport.types";
import { uploadFile } from "../../features/FileUpload/utils";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { removeWhitespaces } from "../../helpers";
import {
  setInsuranceReportsListLoading,
  setInsuranceReportsListSuccess,
  setInsuranceReportsListFailed,
  setInsuranceReportsListV2Loading,
  setInsuranceReportsListV2Success,
  setInsuranceReportsListV2Failed,
} from "./insuranceReportsReducer";
import {
  fetchNoPolicyInsuranceReport,
} from "./insuranceReportsThunk";

// Re-export for backward compatibility
export { clearInsuranceReportsState as clearInsuranceReportState } from "./insuranceReportsReducer";

const execGroupData = (
  data: IInsuranceReports[],
): IGroupedInsuranceReports[] => {
  const groupedData = Object.values(
    data.reduce(
      (acc: Record<number, IGroupedInsuranceReports>, obj) => {
        if (!acc[obj.customer_policy_id]) {
          acc[obj.customer_policy_id] = {
            ...obj,
            childs: [obj],
          };
        } else {
          acc[obj.customer_policy_id].childs.push(obj);
        }
        return acc;
      },
      {},
    ),
  );
  return groupedData;
};

export const fetchInsuranceReportsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    status: string,
    date: IDate,
    searchValue: string,
    searchType: string[],
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(setInsuranceReportsListLoading());
      let url = "";
      if (status !== "-1") {
        url = "&column=request_status&value=" + status;
      }
      let finalUrl = `/tasks/get-tasks-driver-insurance-report?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}${url}`;
      if (searchType.length > 0 && searchValue) {
        finalUrl = `/tasks/get-tasks-driver-insurance-report?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&column=${[searchType]}&value=${searchValue}`;
      }
      const res = await api.get(finalUrl);

      const data: IInsuranceReports[] = res.data.data;
      const finalData = execGroupData(data);
      const totalRecords = res.data.totalRecords;

      dispatch(
        setInsuranceReportsListSuccess({ list: finalData, totalRecords }),
      );
    } catch (err: any) {
      dispatch(setInsuranceReportsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchInsuranceReportsListV2Async =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDate,
    searchValue: IDatatableV2AdvancedSearchFilter,
    compliantStatus: string,
    leadId?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(setInsuranceReportsListV2Loading());
      let finalUrl = `/tasks/get-tasks-driver-insurance-report-v2?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/tasks/get-tasks-driver-insurance-report-v2?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&column=request_status&value=" + status;
      }
      if (compliantStatus !== "-1") {
        finalUrl += "&status=" + compliantStatus;
      }
      if (leadId) {
        finalUrl += "&lead_id=" + leadId;
      }
      const res = await api.get(finalUrl);

      const data: IInsuranceReports[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(
        setInsuranceReportsListV2Success({ listV2: data, totalRecordsV2: totalRecords }),
      );
    } catch (err: any) {
      dispatch(setInsuranceReportsListV2Failed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

/**
 * @deprecated Use `fetchNoPolicyInsuranceReportsList` thunk from insuranceReportsThunk.ts instead.
 */
export const fetchNoPolicyInsuranceReportsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: { fromDate: string; toDate: string },
    searchValue: IDatatableV2AdvancedSearchFilter,
    compliantStatus: string,
    leadId?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(setInsuranceReportsListV2Loading());

      let finalUrl = `/policy/get-no-policy-driver-insurance-report?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/policy/get-no-policy-driver-insurance-report?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&column=request_status&value=" + status;
      }
      if (compliantStatus !== "-1") {
        finalUrl += "&status=" + compliantStatus;
      }
      if (leadId) {
        finalUrl += "&lead_id=" + leadId;
      }
      const res = await api.get(finalUrl);

      const data: INoPolicyInsuranceReports[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(
        setInsuranceReportsListV2Success({
          listV2: data as unknown as IInsuranceReports[],
          totalRecordsV2: totalRecords,
        }),
      );
    } catch (err: any) {
      dispatch(setInsuranceReportsListV2Failed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const addNoPolicyDriverInsuranceReportAsync =
  (
    data: INoPolicyInsuranceReports,
    file: any | null,
    file_two: any | null,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        license_number: data.license_number
          ? data.license_number.replace(/\s+/g, "")
          : null,
      };
      const path = await uploadFile(
        file,
        "NO_POLICY",
        data.file_upload || "",
        asPayload,
      );
      const path_two = await uploadFile(
        file_two,
        "NO_POLICY",
        data.file_upload_two || "",
        asPayload,
      );
      await api.post("/policy/edit-no-policy-driver-insurance-report", {
        ...data,
        file_upload: path,
        file_upload_two: path_two,
        license_number: removeWhitespaces(data.license_number),
      });
      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "No Policy Insurance Report saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchNoPolicyDriverInsuranceReportAsync =
  (
    licenseNumber: string,
    incremental_number: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(
      fetchNoPolicyInsuranceReport({
        licenseNumber,
        incrementalNumber: incremental_number,
      }),
    );
  };
